import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { request } from '../../../config/helpers/axios-instance'
import Modal from '../../../components/Modal'

const fieldControlClass =
    'w-full rounded-lg !border-2 !border-solid !border-gray-400 bg-white-50 p-2 text-sm focus:!border-elm-500 focus:outline-none focus:ring-2 focus:ring-elm-500/25 disabled:opacity-60';

const CONSULTATION_TYPES = ['POST_ORDER', 'PRE_ORDER']

const DEFAULT_CONSULTATION_TYPE = 'POST_ORDER'

/**
 * Raise a MyRx doctor consultation for this order.
 *
 * Everything except the operator's choices is resolved server-side from the order — the patient's
 * name, phone and medicines all come from `orderId` — so this takes no order data as props and
 * cannot drift from what the backend actually sends to MyRx.
 */
const RaiseMyRxConsultationButton = ({ orderId }) => {
    const [showModal, setShowModal] = useState(false)
    const [consultationType, setConsultationType] = useState(DEFAULT_CONSULTATION_TYPE)
    const [caregiverName, setCaregiverName] = useState('')
    const [caregiverPhoneNumber, setCaregiverPhoneNumber] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const resetForm = () => {
        setConsultationType(DEFAULT_CONSULTATION_TYPE)
        setCaregiverName('')
        setCaregiverPhoneNumber('')
    }

    const handleClose = () => {
        if (submitting) return
        setShowModal(false)
        resetForm()
    }

    // The phone is the key of the backend's pending-consultation guard and the number MyRx
    // dials, so keep it bare 10 digits rather than letting "+91 98765 43210" through.
    const handlePhoneChange = (e) => {
        const rawValue = e.target.value
        setCaregiverPhoneNumber(/^\d+$/.test(rawValue) ? rawValue.slice(0, 10) : rawValue)
    }

    const handleConfirm = async () => {
        if (submitting) return

        const name = caregiverName.trim()
        const phone = caregiverPhoneNumber.trim()
        const hasCaregiver = name !== '' && phone !== ''

        // Mirrors the backend's both-or-neither rule, so a half-filled caregiver is caught here
        // rather than coming back as a generic invalid-parameter error.
        if ((name !== '') !== (phone !== '')) {
            toast.error('Enter both caregiver name and phone, or leave both blank')
            return
        }
        if (hasCaregiver && !/^\d{10}$/.test(phone)) {
            toast.error('Caregiver phone must be 10 digits')
            return
        }

        setSubmitting(true)
        try {
            const res = await request({
                url: 'myRx/placeOrderConsultationRequest',
                method: 'POST',
                data: {
                    orderId: parseInt(orderId, 10),
                    consultationType,
                    ...(hasCaregiver && { caregiverName: name, caregiverPhoneNumber: phone }),
                },
            })

            if (res?.data?.success) {
                toast.success('Doctor consultation raised')
                setShowModal(false)
                resetForm()
            } else {
                // The envelope's key is `error`, not `message` — and the "already pending"
                // text it carries is the main thing this modal has to tell the operator, so
                // the modal deliberately stays open on failure.
                toast.error(res?.data?.error || 'Failed to raise doctor consultation')
            }
        } catch (error) {
            toast.error('Failed to reach MyRx. You can retry.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <>
            <button
                type="button"
                className="p-2 bg-[#902b8b] rounded-xl text-white-50 text-center text-xl flex-auto cursor-pointer hover:bg-[#7a2478] transition-colors"
                onClick={() => setShowModal(true)}
            >
                Raise Rx Consultation
            </button>

            {showModal && (
                <Modal title="Raise Doctor Consultation" onClose={handleClose}>
                    <div className="py-2">
                        <fieldset className="mb-5">
                            <legend className="mb-2 text-sm font-semibold">Consultation type</legend>
                            {CONSULTATION_TYPES.map((option) => (
                                <label key={option} className="mb-2 flex cursor-pointer items-center gap-2">
                                    <input
                                        type="radio"
                                        name="consultationType"
                                        value={option}
                                        checked={consultationType === option}
                                        onChange={() => setConsultationType(option)}
                                        disabled={submitting}
                                    />
                                    <span className="text-sm font-medium">{option}</span>
                                </label>
                            ))}
                        </fieldset>

                        <div className="mb-4">
                            <p className="mb-1 text-sm font-semibold">Caregiver (optional)</p>
                            <p className="mb-3 text-xs text-gray-500">
                                If filled, the caregiver is the person MyRx contacts and the patient is listed as the
                                patient. Fill both or neither.
                            </p>
                            <div className="flex flex-col gap-3 sm:flex-row">
                                <div className="flex-1">
                                    <label htmlFor="caregiverName" className="mb-1 block text-xs">Caregiver name</label>
                                    <input
                                        id="caregiverName"
                                        type="text"
                                        className={fieldControlClass}
                                        value={caregiverName}
                                        onChange={(e) => setCaregiverName(e.target.value)}
                                        disabled={submitting}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label htmlFor="caregiverPhoneNumber" className="mb-1 block text-xs">Caregiver phone</label>
                                    <input
                                        id="caregiverPhoneNumber"
                                        type="text"
                                        inputMode="numeric"
                                        className={fieldControlClass}
                                        value={caregiverPhoneNumber}
                                        onChange={handlePhoneChange}
                                        disabled={submitting}
                                        placeholder="10-digit phone"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={submitting}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={submitting}
                                className={`px-4 py-2 bg-[#902b8b] text-white-50 rounded font-semibold hover:bg-[#7a2478] transition-colors ${submitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                {submitting ? 'Raising…' : 'Raise request'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    )
}

RaiseMyRxConsultationButton.propTypes = {
    orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export default RaiseMyRxConsultationButton
